/**
 * Client-side translations.
 *
 * The server drops a small dictionary into the page; the bundle reads it at
 * startup. The strings built in JavaScript - selector placeholders, emoji
 * picker labels, confirmation buttons - otherwise stayed in French whatever
 * language was chosen.
 *
 * Only the keys the client genuinely uses are exposed: there is no point
 * shipping the 400 entries of the server dictionary in every page.
 */

type ClientStrings = Record<string, string>;

let strings: ClientStrings = {};

/** Reads the JSON block dropped in by the template. Silent when it is absent. */
export function loadClientStrings(): void {
  const node = document.getElementById('i18n-data');
  if (!node?.textContent) return;

  try {
    strings = JSON.parse(node.textContent) as ClientStrings;
  } catch {
    // Corrupted block: we keep the fallback values passed to `ct()`.
  }
}

/**
 * Translates a key, or returns `fallback`.
 *
 * The fallback is mandatory: it guarantees that a key forgotten on the server
 * side never displays an empty label in the interface.
 *
 * `values` substitutes the `{name}` tokens, following the same convention as
 * the server-side `t()`. Without it, any label carrying a value had to be built
 * by concatenation - which is how `Retirer ${role.name}` stayed French in every
 * language, since a concatenated string has no key to translate.
 */
export function ct(key: string, fallback: string, values?: Record<string, string>): string {
  const template = strings[key] ?? fallback;
  if (!values) return template;

  return template.replace(/\{(\w+)\}/g, (token, name: string) => values[name] ?? token);
}
